/**
 * Distance display formatting — the single source of truth for rendering
 * distances (mirrors the currencySymbol pattern in config/currency.js).
 *
 * Storage is ALWAYS metric (meters / km) — the DB, walking-speed math, and
 * radius queries never change with the user's preference. Only display
 * converts. The unit comes from settings.units.distance via
 * SettingsService.getDistanceUnit() / the useDistanceUnit() hook: 'metric'
 * or 'imperial', with 'auto' resolved from the user's home country before it
 * reaches here (UK resolves to imperial for distance but Celsius for
 * temperature — which is why distance and temperature are separate
 * preferences).
 */

const METERS_PER_MILE = 1609.344;
const FEET_PER_METER = 3.28084;

/**
 * Format a distance in meters for display.
 * metric:   "650 m" below 1 km, else "1.2 km"
 * imperial: "400 ft" below 0.1 mi (Google Maps convention, rounded to 10 ft),
 *           else "0.7 mi"
 * Returns '' for null/undefined so callers can `|| fallback`.
 */
export function formatDistance(meters, unit = 'metric') {
  if (meters === null || meters === undefined || Number.isNaN(Number(meters))) return '';
  const m = Number(meters);

  if (unit === 'imperial') {
    const miles = m / METERS_PER_MILE;
    if (miles < 0.1) {
      return `${Math.max(10, Math.round((m * FEET_PER_METER) / 10) * 10)} ft`;
    }
    return `${miles.toFixed(1)} mi`;
  }

  if (m < 1000) return `${Math.round(m)} m`;
  return `${(m / 1000).toFixed(1)} km`;
}

/** Convenience for data stored in kilometers. */
export function formatDistanceKm(km, unit = 'metric') {
  if (km === null || km === undefined || Number.isNaN(Number(km))) return '';
  return formatDistance(Number(km) * 1000, unit);
}
